import { useEffect, useRef, useCallback } from 'react';
import { fileAPI } from '../services/api/fileApi';
import type { FileProgressResponse } from '../types/fileUpload';

// 轮询配置：指数退避策略
const INITIAL_INTERVAL = 1000; // 1秒
const MAX_INTERVAL = 30000; // 30秒（最大值）
const BACKOFF_MULTIPLIER = 2; // 每次翻倍

interface ProcessingFile {
  id: string;
  fileId: string;
}

interface UseFileProcessingPollingOptions {
  // 轮询的文件列表
  processingFiles: ProcessingFile[];
  // 当文件状态更新时的回调 - 返回是否应该继续轮询该文件
  onFileStatusUpdate?: (
    fileId: string,
    status: 'processing' | 'completed' | 'error',
    data: FileProgressResponse
  ) => boolean; // 返回 true 表示继续轮询，false 表示停止轮询
  // 当所有文件都处理完成时的回调
  onAllFilesCompleted?: () => void;
  // 是否启用轮询
  enabled?: boolean;
}

/**
 * 文件处理状态轮询 Hook
 *
 * 使用指数退避策略轮询文件处理状态：
 * - 初始间隔：1秒
 * - 每次失败后间隔翻倍：1s -> 2s -> 4s -> 8s -> 16s -> 30s
 * - 达到最大值后保持30秒
 *
 * @example
 * ```tsx
 * const { startPolling, stopPolling } = useFileProcessingPolling({
 *   processingFiles: files.filter(f => f.status === 'processing').map(f => ({ id: f.id, fileId: f.fileId })),
 *   onFileStatusUpdate: (fileId, status, data) => {
 *     console.log(`File ${fileId} status: ${status}`);
 *     return status === 'processing'; // 只有 processing 状态才继续轮询
 *   },
 *   onAllFilesCompleted: () => {
 *     console.log('All files completed');
 *   },
 * });
 * ```
 */
export function useFileProcessingPolling({
  processingFiles,
  onFileStatusUpdate,
  onAllFilesCompleted,
  enabled = true,
}: UseFileProcessingPollingOptions) {
  const timersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const intervalRef = useRef<Map<string, number>>(new Map());
  const processingFilesRef = useRef(processingFiles);
  const onFileStatusUpdateRef = useRef(onFileStatusUpdate);
  const onAllFilesCompletedRef = useRef(onAllFilesCompleted);
  const enabledRef = useRef(enabled);
  const isMountedRef = useRef(true);

  // 保持 refs 最新
  useEffect(() => {
    processingFilesRef.current = processingFiles;
    onFileStatusUpdateRef.current = onFileStatusUpdate;
    onAllFilesCompletedRef.current = onAllFilesCompleted;
    enabledRef.current = enabled;
  }, [processingFiles, onFileStatusUpdate, onAllFilesCompleted, enabled]);

  // 计算退避间隔
  const calculateBackoffInterval = useCallback((currentInterval: number): number => {
    const nextInterval = currentInterval * BACKOFF_MULTIPLIER;
    return Math.min(nextInterval, MAX_INTERVAL);
  }, []);

  // 停止单个文件的轮询
  const stopPollingFile = useCallback((fileId: string) => {
    const timer = timersRef.current.get(fileId);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(fileId);
    }
    intervalRef.current.delete(fileId);
  }, []);

  // 停止所有轮询
  const stopPolling = useCallback(() => {
    timersRef.current.forEach(timer => clearTimeout(timer));
    timersRef.current.clear();
    intervalRef.current.clear();
  }, []);

  // 轮询单个文件
  const pollFile = useCallback(
    async (file: ProcessingFile, currentInterval: number = INITIAL_INTERVAL) => {
      // 检查是否仍然启用且组件仍然挂载
      if (!enabledRef.current || !isMountedRef.current) return;

      try {
        const response = await fileAPI.getFileProgress(file.fileId);
        const data = response.data;

        // 在更新状态前再次检查组件是否仍然挂载
        if (!isMountedRef.current) return;

        // 通知状态更新，检查是否应该继续轮询
        const shouldContinue = onFileStatusUpdateRef.current?.(file.fileId, data.status, data);

        if (shouldContinue === false || data.status === 'error') {
          // 文件处理完成或出错，停止轮询
          stopPollingFile(file.fileId);

          // 检查是否所有文件都已完成
          const remainingFiles = Array.from(timersRef.current.keys()).filter(
            id => id !== file.fileId
          );
          if (remainingFiles.length === 0 && isMountedRef.current) {
            onAllFilesCompletedRef.current?.();
          }
        } else if (isMountedRef.current && enabledRef.current) {
          // 继续轮询，使用指数退避
          const nextInterval = calculateBackoffInterval(currentInterval);
          intervalRef.current.set(file.fileId, nextInterval);

          const timer = setTimeout(() => {
            pollFile(file, nextInterval);
          }, nextInterval);
          timersRef.current.set(file.fileId, timer);
        }
      } catch (error) {
        console.error(`Failed to poll file ${file.fileId}:`, error);

        // 在重试前检查组件是否仍然挂载
        if (!isMountedRef.current || !enabledRef.current) return;

        // 出错时也使用指数退避重试
        const nextInterval = calculateBackoffInterval(currentInterval);
        intervalRef.current.set(file.fileId, nextInterval);

        const timer = setTimeout(() => {
          pollFile(file, nextInterval);
        }, nextInterval);
        timersRef.current.set(file.fileId, timer);
      }
    },
    [stopPollingFile, calculateBackoffInterval]
  );

  // 开始轮询所有处理中的文件
  const startPolling = useCallback(() => {
    if (!enabledRef.current || !isMountedRef.current) return;

    // 停止之前的轮询
    stopPolling();

    // 为每个处理中的文件启动轮询
    processingFilesRef.current.forEach(file => {
      pollFile(file, INITIAL_INTERVAL);
    });
  }, [stopPolling, pollFile]);

  // 当 processingFiles 变化时重新启动轮询
  useEffect(() => {
    isMountedRef.current = true;
    enabledRef.current = enabled;

    if (enabled && processingFiles.length > 0) {
      startPolling();
    } else if (processingFiles.length === 0) {
      // 没有处理中的文件，停止所有轮询
      stopPolling();
    }

    return () => {
      // 组件卸载时停止所有轮询
      isMountedRef.current = false;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, processingFiles.length, startPolling, stopPolling]);

  return {
    startPolling,
    stopPolling,
    // 获取当前轮询间隔（用于调试）
    getPollingIntervals: () => new Map(intervalRef.current),
  };
}
